import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    ViewEncapsulation,
    computed,
    inject,
    input,
    model,
    signal,
    viewChild,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { statusIcons } from './assets/status-icons';

export const DS_TOAST_TYPE = ['info', 'caution', 'error', 'success', 'warning'] as const;
export type DsToastType = (typeof DS_TOAST_TYPE)[number];

export const DS_TOAST_VARIANT = ['subtle', 'strong'] as const;
export type DsToastVariant = (typeof DS_TOAST_VARIANT)[number];
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'ds-toast',
    template: `<div
        class="ds-toast-wrapper"
        [class.ds-toast-has-title]="contentState().hasTitle"
        [class.ds-toast-has-description]="contentState().hasDescription">
        <div class="ds-toast-status-icon" [innerHTML]="svg()"></div>
        <div class="ds-toast-title" #titleContentWrapper>
            <ng-content select="[slot=title]" />
        </div>
        <div class="ds-toast-description" #descContentWrapper>
            <ng-content />
        </div>
        <div class="ds-toast-actions">
            <div class="ds-toast-action">
                <ng-content select="[slot=action]" />
            </div>
            <div class="ds-toast-close">
                <ng-content select="[slot=close]" />
            </div>
        </div>
    </div>`,
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    styleUrl: './toast.component.scss',
    host: {
        'class': 'ds-toast',
        '[class]': 'hostClass()',
        '[attr.role]': 'role()',
        '[attr.aria-atomic]': '"true"',
    },
})
export class DsToast implements AfterViewInit {
    public readonly type = input<DsToastType>('info');
    public readonly variant = model<DsToastVariant>('subtle');

    public readonly politeness = input<'polite' | 'assertive'>('assertive');

    private readonly sanitizer = inject(DomSanitizer);

    protected readonly titleContentWrapperRef = viewChild('titleContentWrapper', { read: ElementRef });
    protected readonly descContentWrapperRef = viewChild('descContentWrapper', { read: ElementRef });
    protected readonly contentState = signal({ hasTitle: false, hasDescription: false });

    protected readonly svg = computed<SafeHtml>(() => {
        const toastType = this.type() === 'warning' ? 'caution' : this.type();
        const rawSvg = statusIcons[toastType];
        return this.sanitizer.bypassSecurityTrustHtml(rawSvg);
    });

    protected readonly hostClass = computed(() => {
        const type = this.type();
        const variant = this.variant();
        return `ds-toast-${type === 'warning' ? 'caution' : type} ds-toast-${variant}`;
    });

    ngAfterViewInit() {
        const titleEl = this.titleContentWrapperRef()?.nativeElement;
        const descEl = this.descContentWrapperRef()?.nativeElement;

        this.contentState.set({
            hasTitle: !!titleEl?.textContent?.trim(),
            hasDescription: !!descEl?.textContent?.trim(),
        });
    }

    protected readonly role = computed(() => (this.politeness() === 'assertive' ? 'alert' : 'status'));
}
